import type { AvailabilityDto, AvailabilitySlotDto } from "@clinic/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "./api";
import { addDays, formatDate, formatTime, localDate, useT } from "./i18n";
import { Alert, Button, Spinner } from "./ui";

/**
 * Week-at-a-time picker over the public availability endpoint. Used by the
 * booking page and by customers moving an existing booking.
 */
export function SlotPicker({
  slug,
  serviceId,
  practitionerId,
  timezone,
  selected,
  onSelect,
}: {
  slug: string;
  serviceId: string;
  practitionerId?: string;
  timezone: string;
  selected: AvailabilitySlotDto | null;
  onSelect: (slot: AvailabilitySlotDto) => void;
}) {
  const { t, locale } = useT();
  const today = localDate(Date.now(), timezone);
  const [weekStart, setWeekStart] = useState(today);
  const [day, setDay] = useState<string | null>(null);
  const weekEnd = addDays(weekStart, 6);

  const availability = useQuery({
    queryKey: ["availability", slug, serviceId, practitionerId ?? "any", weekStart],
    queryFn: () =>
      api<AvailabilityDto>(
        "GET",
        `/public/clinics/${slug}/availability?serviceId=${serviceId}${practitionerId ? `&practitionerId=${practitionerId}` : ""}&from=${weekStart}&to=${weekEnd}`,
      ),
  });

  const days = availability.data?.days ?? [];
  const firstWithSlots = days.find((d) => d.slots.length > 0)?.date ?? null;
  const activeDay = day && days.some((d) => d.date === day) ? day : firstWithSlots;
  const activeSlots = days.find((d) => d.date === activeDay)?.slots ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="secondary"
          disabled={weekStart <= today}
          onClick={() => setWeekStart(addDays(weekStart, -7))}
        >
          ‹ {t("common.previous")}
        </Button>
        <p className="text-sm font-medium text-slate-700">
          {formatDate(locale, `${weekStart}T12:00:00Z`, "UTC", { weekday: undefined })} –{" "}
          {formatDate(locale, `${weekEnd}T12:00:00Z`, "UTC", { weekday: undefined, year: "numeric" })}
        </p>
        <Button type="button" variant="secondary" onClick={() => setWeekStart(addDays(weekStart, 7))}>
          {t("common.next")} ›
        </Button>
      </div>

      {availability.isPending ? (
        <div className="flex justify-center p-6 text-brand-700">
          <Spinner />
        </div>
      ) : availability.isError ? (
        <Alert>{t("common.error_load")}</Alert>
      ) : !firstWithSlots ? (
        <p className="rounded-lg bg-slate-50 p-6 text-center text-sm text-slate-600">{t("book.no_slots_week")}</p>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1" role="tablist">
            {days.map((d) => {
              const label = new Date(`${d.date}T12:00:00Z`);
              const isActive = d.date === activeDay;
              return (
                <button
                  key={d.date}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  disabled={d.slots.length === 0}
                  onClick={() => setDay(d.date)}
                  className={`rounded-lg px-1 py-2 text-center text-xs ring-1 disabled:opacity-40 ${
                    isActive
                      ? "bg-brand-700 text-white ring-brand-700"
                      : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <span className="block">
                    {label.toLocaleDateString(locale === "da" ? "da-DK" : "en-GB", {
                      weekday: "short",
                      timeZone: "UTC",
                    })}
                  </span>
                  <span className="block text-base font-semibold">{label.getUTCDate()}</span>
                  <span className="block opacity-80">{d.slots.length || "–"}</span>
                </button>
              );
            })}
          </div>
          {activeSlots.length === 0 ? (
            <p className="text-sm text-slate-500">{t("book.no_slots_day")}</p>
          ) : (
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
              {activeSlots.map((s) => {
                const isSelected = selected?.startsAt === s.startsAt;
                return (
                  <button
                    key={s.startsAt}
                    type="button"
                    data-slot={s.startsAt}
                    aria-pressed={isSelected}
                    onClick={() => onSelect(s)}
                    className={`rounded-lg px-2 py-2 text-sm font-medium ring-1 ${
                      isSelected
                        ? "bg-brand-700 text-white ring-brand-700"
                        : "bg-white text-slate-800 ring-slate-300 hover:bg-brand-50"
                    }`}
                  >
                    {formatTime(locale, s.startsAt, timezone)}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
      <p className="text-xs text-slate-500">{t("book.times_in", { tz: timezone })}</p>
    </div>
  );
}
